import { useState } from 'react';
import { motion } from 'framer-motion';
import { X, Check, Plus, Zap, Play, Layers, Copy } from 'lucide-react';
import { getStripeCheckoutUrl } from '../data/checkoutLinks';

// Upgrade definitions
const allUpgrades = {
  'fast-turnaround': {
    id: 'fast-turnaround',
    name: 'Fast Turnaround',
    description: 'Priority delivery for time-sensitive releases, events, or last-minute changes.',
    icon: Zap
  },
  'motion-graphics': {
    id: 'motion-graphics',
    name: 'Motion Graphics',
    description: 'Animated visuals for reels, stories, screens, and ads.',
    icon: Play
  },
  'additional-concepts': {
    id: 'additional-concepts',
    name: 'Additional Concepts',
    description: 'Explore multiple creative directions before locking the final design.',
    icon: Layers
  },
  'additional-formats': {
    id: 'additional-formats',
    name: 'Additional Formats',
    description: 'Extra size or platform versions beyond what\'s included in the package.',
    icon: Copy
  }
};

// Visibility rules per identity and formula
const upgradeVisibility = {
  artist: {
    '01': ['fast-turnaround', 'motion-graphics', 'additional-concepts'],
    '02': ['fast-turnaround', 'motion-graphics', 'additional-concepts'],
    '03': ['fast-turnaround', 'motion-graphics', 'additional-concepts']
  },
  business: {
    '01': ['fast-turnaround', 'additional-concepts'],
    '02': ['fast-turnaround', 'motion-graphics', 'additional-concepts', 'additional-formats'],
    '03': ['fast-turnaround', 'motion-graphics', 'additional-concepts', 'additional-formats']
  },
  events: {
    '01': ['fast-turnaround', 'motion-graphics', 'additional-concepts', 'additional-formats']
    // Formula 02 and 03 are call-only, no upgrades shown
  }
};

const UpgradesModal = ({ 
  isOpen, 
  onClose, 
  selectedPackage, 
  selectedIdentity,
  onContinue,
  onSkip 
}) => {
  const [selectedUpgrades, setSelectedUpgrades] = useState([]);
  
  if (!isOpen || !selectedPackage || !selectedIdentity) return null;
  
  // Get available upgrades for this package
  const availableUpgradeIds = upgradeVisibility[selectedIdentity]?.[selectedPackage.formula] || [];
  const availableUpgrades = availableUpgradeIds.map(id => allUpgrades[id]);
  
  const handleUpgradeToggle = (upgrade) => {
    setSelectedUpgrades(prev => 
      prev.some(u => u.id === upgrade.id)
        ? prev.filter(u => u.id !== upgrade.id)
        : [...prev, upgrade]
    );
  };
  
  const handleContinue = () => {
    // Get the Stripe checkout URL based on identity, formula, and upgrades
    const checkoutUrl = getStripeCheckoutUrl(
      selectedIdentity,
      selectedPackage.formula,
      selectedUpgrades
    );
    // Redirect to Stripe checkout
    window.open(checkoutUrl, '_blank');
    onContinue(selectedUpgrades);
  };
  
  const handleSkip = () => {
    // Skip upgrades, go to base checkout
    const checkoutUrl = getStripeCheckoutUrl(
      selectedIdentity,
      selectedPackage.formula,
      []
    );
    window.open(checkoutUrl, '_blank');
    onSkip();
  };

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      transition={{ duration: 0.4, ease: [0.4, 0, 0.2, 1] }}
      className="fixed inset-0 z-50 flex items-center justify-center p-4"
    >
      {/* Backdrop - Subtle dim */}
      <motion.div 
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        transition={{ duration: 0.4 }}
        className="absolute inset-0 bg-background/90 backdrop-blur-sm"
        onClick={onClose}
      />
      
      {/* Modal - Premium entrance */}
      <motion.div
        initial={{ opacity: 0, y: 24 }}
        animate={{ opacity: 1, y: 0 }}
        exit={{ opacity: 0, y: 16 }}
        transition={{ duration: 0.5, ease: [0.4, 0, 0.2, 1] }}
        className="relative w-full max-w-2xl bg-card border border-border z-10 max-h-[90vh] overflow-y-auto"
      >
        {/* Close button */}
        <button
          onClick={onClose}
          className="absolute top-6 right-6 p-2 text-muted-foreground hover:text-foreground transition-colors duration-300 z-10"
        >
          <X className="w-5 h-5" />
        </button>
        
        {/* Content */}
        <div className="p-8 md:p-10">
          {/* Header */}
          <div className="mb-10">
            <p className="font-body text-xs tracking-[0.2em] text-accent uppercase mb-3">
              FORMULA {selectedPackage.formula}
            </p>
            <h3 className="font-display text-3xl text-foreground mb-3">UPGRADES</h3>
            <p className="font-body text-sm text-muted-foreground">
              Enhance your package with optional upgrades.
            </p>
          </div>
          
          {/* Package summary */}
          <div className="bg-muted/20 p-5 mb-8 border border-border/50">
            <div className="flex justify-between items-center">
              <span className="font-body text-sm text-muted-foreground">{selectedPackage.name}</span>
              <span className="font-display text-xl text-foreground">${selectedPackage.price}</span>
            </div>
          </div>
          
          {/* Upgrades list */}
          <div className="space-y-3 mb-10">
            {availableUpgrades.map((upgrade) => {
              const Icon = upgrade.icon;
              const isSelected = selectedUpgrades.some(u => u.id === upgrade.id);
              
              return (
                <button
                  key={upgrade.id}
                  onClick={() => handleUpgradeToggle(upgrade)}
                  className={`w-full flex items-center gap-4 p-5 text-left border transition-colors duration-400 ${
                    isSelected 
                      ? 'border-accent/60 bg-accent/5' 
                      : 'border-border hover:border-muted-foreground/40'
                  }`}
                  style={{ transitionTimingFunction: 'cubic-bezier(0.4, 0, 0.2, 1)' }}
                >
                  {/* Icon */}
                  <div className={`p-2.5 rounded transition-colors duration-300 ${
                    isSelected ? 'bg-accent text-background' : 'bg-muted/50 text-foreground'
                  }`}>
                    <Icon className="w-5 h-5" />
                  </div>
                  
                  {/* Content */}
                  <div className="flex-grow">
                    <h4 className="font-display text-lg text-foreground mb-1">
                      {upgrade.name}
                    </h4>
                    <p className="font-body text-sm text-muted-foreground leading-relaxed">
                      {upgrade.description}
                    </p>
                  </div>
                  
                  {/* Selection indicator */}
                  <div className={`w-6 h-6 rounded-full border-2 flex items-center justify-center transition-all duration-300 flex-shrink-0 ${
                    isSelected 
                      ? 'border-accent bg-accent text-background' 
                      : 'border-muted-foreground/20'
                  }`}>
                    {isSelected ? <Check className="w-3 h-3" /> : <Plus className="w-3 h-3 text-muted-foreground/30" />}
                  </div>
                </button>
              );
            })}
          </div>
          
          {/* Action buttons - Confident, not pushy */}
          <div className="flex flex-col sm:flex-row gap-4">
            <button
              onClick={handleContinue}
              className="flex-1 py-4 font-display text-sm tracking-wider bg-foreground text-background hover:bg-accent transition-colors duration-400"
              style={{ transitionTimingFunction: 'cubic-bezier(0.4, 0, 0.2, 1)' }}
            >
              CONTINUE TO CHECKOUT
            </button>
            <button
              onClick={handleSkip}
              className="flex-1 py-4 font-display text-sm tracking-wider bg-transparent text-muted-foreground border border-border hover:text-foreground hover:border-muted-foreground transition-colors duration-400"
              style={{ transitionTimingFunction: 'cubic-bezier(0.4, 0, 0.2, 1)' }}
            >
              SKIP
            </button>
          </div>
        </div>
      </motion.div>
    </motion.div>
  );
};

export default UpgradesModal;
